import { apiFetch } from '../../services/http';

export type Conversation = {
  id: string;
  subject: string;
  participants: string[];
  createdAt: string;
  updatedAt: string;
};

export type Message = {
  id: string;
  conversationId: string;
  authorAlias: string;
  body?: string;           // Plaintext body (optional, for backwards compatibility)
  encryptedBody?: string;  // Encrypted body (E2E encrypted messages)
  createdAt: string;
};

export type ConversationWithMessages = {
  conversation: Conversation;
  messages: Message[];
};

const ALIAS_HEADER = 'X-User-Alias';

function authHeaders(alias: string) {
  return {
    [ALIAS_HEADER]: alias,
    'Content-Type': 'application/json',
  };
}

export async function fetchConversations(alias: string, limit = 50): Promise<Conversation[]> {
  return apiFetch<Conversation[]>(`/api/messaging/conversations?limit=${limit}`, {
    headers: authHeaders(alias),
  });
}

export async function createConversation(alias: string, payload: { subject: string; participants: string[] }): Promise<Conversation> {
  return apiFetch<Conversation>(`/api/messaging/conversations`, {
    method: 'POST',
    headers: authHeaders(alias),
    body: JSON.stringify(payload),
  });
}

export async function fetchConversation(alias: string, conversationId: string): Promise<ConversationWithMessages> {
  return apiFetch<ConversationWithMessages>(`/api/messaging/conversations/${conversationId}/`, {
    headers: authHeaders(alias),
  });
}

export async function fetchMessages(alias: string, conversationId: string, limit = 100): Promise<Message[]> {
  return apiFetch<Message[]>(`/api/messaging/conversations/${conversationId}/messages?limit=${limit}`, {
    headers: authHeaders(alias),
  });
}

export async function sendMessage(alias: string, conversationId: string, body: string): Promise<Message> {
  return apiFetch<Message>(`/api/messaging/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: authHeaders(alias),
    body: JSON.stringify({ body }),
  });
}

// Alias management

export type AliasCheckResponse = {
  available: boolean;
  alias: string;
  message?: string;
};

export async function checkAliasAvailability(alias: string): Promise<AliasCheckResponse> {
  return apiFetch<AliasCheckResponse>(`/api/messaging/alias/check?alias=${encodeURIComponent(alias)}`, {
    method: 'GET',
  });
}

export async function registerAlias(alias: string, publicKey?: string): Promise<{ success: boolean; alias: string; userId: string }> {
  return apiFetch<{ success: boolean; alias: string; userId: string }>(`/api/messaging/alias`, {
    method: 'POST',
    body: JSON.stringify({ alias, publicKey }),
  });
}

export async function getUserIdByAlias(alias: string): Promise<{ alias: string; userId: string }> {
  return apiFetch<{ alias: string; userId: string }>(`/api/messaging/alias/${encodeURIComponent(alias)}/user`, {
    method: 'GET',
  });
}
